<template>
  <div class="relative w-full h-full overflow-y-auto p-4">
    <div class="bg-white rounded-lg p-6 mb-4 w-full">
      <h2 class="font-bold text-xl mb-4">Berechtigungen</h2>

      <section class="mb-6">
        <div class="flex items-center justify-between mb-2">
          <h3 class="font-bold">Vergebene Berechtigungen</h3>
          <span class="text-xs text-gray-500">{{ usersWithPermissions.length }} Nutzer</span>
        </div>
        <div class="h-64 overflow-auto border border-gray-200 rounded">
          <table class="w-full text-left text-sm whitespace-nowrap">
            <thead class="sticky top-0 bg-gray-100 border-b border-gray-200">
              <tr>
                <th class="px-3 py-2 sticky left-0 bg-gray-100 z-10">Nutzer</th>
                <th
                  v-for="field in usedPermissionFields"
                  :key="field.key"
                  class="px-3 py-2 text-center"
                >
                  {{ field.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="u in usersWithPermissions"
                :key="u.id"
                @click="selectUser(u.mail)"
                class="border-b border-gray-100 cursor-pointer group"
                :class="selectedMail === u.mail ? 'bg-blue-100' : 'hover:bg-blue-50'"
              >
                <td
                  class="px-3 py-2 sticky left-0"
                  :class="selectedMail === u.mail ? 'bg-blue-100 font-bold' : 'bg-white group-hover:bg-blue-50'"
                >
                  {{ u.mail }}
                </td>
                <td
                  v-for="field in usedPermissionFields"
                  :key="field.key"
                  class="px-3 py-2 text-center"
                >
                  <span v-if="permissionValue(u, field.key) > 0">
                    {{ permissionLevelLabel(field.key, permissionValue(u, field.key)) }}
                  </span>
                  <span v-else class="text-gray-300">–</span>
                </td>
              </tr>
              <tr v-if="usersWithPermissions.length === 0">
                <td :colspan="usedPermissionFields.length + 1" class="px-3 py-4 text-center text-gray-500">
                  Keine Berechtigungen vergeben.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <div class="mb-4">
        <label>Nutzer auswählen</label>
        <input
          type="text"
          v-model="userFilter"
          @input="onFilterInput"
          placeholder="E-Mail suchen..."
          class="border-2 border-gray-200 px-2 py-1 w-full max-w-md mb-2"
        >
        <div v-if="showUserList" class="border-2 border-gray-200 rounded max-h-64 overflow-y-auto w-full max-w-md">
          <div
            v-for="u in filteredUsers"
            :key="u.id"
            @click="selectUser(u.mail)"
            class="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100"
            :class="selectedMail === u.mail ? 'bg-blue-100 font-bold' : ''"
          >
            {{ u.mail }}
          </div>
          <div v-if="filteredUsers.length === 0" class="px-3 py-4 text-gray-500 text-center text-sm">
            Keine Nutzer gefunden.
          </div>
          <div class="px-3 py-2 text-center text-xs text-gray-400 border-t">
            {{ filteredUsers.length }} von {{ users.length }} Nutzern
          </div>
        </div>
        <div v-if="selectedMail && !showUserList" class="flex items-center gap-2 mb-2">
          <span class="text-sm font-bold">{{ selectedMail }}</span>
          <button @click="clearSelection" class="text-sm text-red-600 hover:underline">Wechseln</button>
        </div>
      </div>
      <div v-if="permData && permData.mail">
        <div class="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4 mb-6">
          <div v-for="field in permissionFields" :key="field.key">
            <label>{{ field.label }}</label>
            <select v-model="permData[field.key]" class="border-2 border-gray-200 px-2 py-1 w-full">
              <option v-for="lvl in permissionLevels(field.key)" :key="lvl.value" :value="lvl.value">{{ lvl.label }}</option>
            </select>
          </div>
        </div>
        <div class="mb-4 p-3 bg-gray-100 rounded text-sm">
          <strong>Bedeutungen:</strong> 0 = Kein Zugriff, 1 = Lesen, 2 = Hinzufügen, 3 = Ändern, 4 = Löschen, 5 = Imitieren (nur Kunden)
        </div>
        <div v-if="user.Permissions >= 1" class="flex flex-wrap gap-2">
          <button @click="savePermissions" :disabled="saving || resetting">
            {{ saving ? 'Speichere...' : 'Speichern' }}
          </button>
          <button
            @click="resetPermissions"
            :disabled="saving || resetting"
            class="text-red-600 border border-red-300 px-3 py-1 rounded hover:bg-red-50"
          >
            {{ resetting ? 'Setze zurück...' : 'Alle Berechtigungen löschen' }}
          </button>
        </div>
        <div v-if="message" class="mt-4" :class="messageSuccess ? 'text-green-600' : 'text-red-600'">{{ message }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import API from "@/API";
export default {
  name: "Permissions",
  props: ["user"],
  data() {
    return {
      users: [],
      userFilter: "",
      showUserList: false,
      selectedMail: "",
      permData: null,
      saving: false,
      resetting: false,
      message: null,
      messageSuccess: false,
      permissionFields: [
        { key: "Intern", label: "Interner Bereich" },
        { key: "Dashboard", label: "Dashboard" },
        { key: "Contacts", label: "Kontakte" },
        { key: "Accounts", label: "Accounthilfe" },
        { key: "Customers", label: "Kundenverwaltung" },
        { key: "Employees", label: "Mitarbeiterverwaltung" },
        { key: "Exporte", label: "Dateiexporte" },
        { key: "Projects", label: "Projektverwaltung" },
        { key: "Contracts", label: "Vertragsverwaltung" },
        { key: "Permissions", label: "Berechtigungsvergabe" },
        { key: "UploadProjectFiles", label: "Projektdateien hochladen" },
        { key: "ViewUserFiles", label: "Benutzerdateien anzeigen" },
        { key: "Import", label: "Daten importieren" },
        { key: "Maintenance", label: "Wartungsmodus verwalten" },
        { key: "Umfragen", label: "Umfragen" },
        { key: "Maillist", label: "Verteiler" },
        { key: "Mealplan", label: "Essensplanung" },
        { key: "Newsletter", label: "Newsletter" },
        { key: "Announcements", label: "Ankündigungen" },
        { key: "Attendance", label: "Anwesenheit" },
      ],
    };
  },
  computed: {
    filteredUsers() {
      if (!this.userFilter || this.userFilter.length < 1) {
        return this.users.slice(0, 50);
      }
      const q = this.userFilter.toLowerCase();
      return this.users.filter(u => u.mail.toLowerCase().includes(q)).slice(0, 50);
    },
    usersWithPermissions() {
      return this.users.filter(u => (u.permissions || []).length > 0);
    },
    usedPermissionFields() {
      const grantedKeys = new Set();
      this.usersWithPermissions.forEach(u => {
        u.permissions.forEach(p => grantedKeys.add(p.key));
      });
      return this.permissionFields.filter(f => grantedKeys.has(f.key));
    },
  },
  methods: {
    onFilterInput() {
      this.showUserList = true;
    },
    selectUser(mail) {
      this.selectedMail = mail;
      this.showUserList = false;
      this.userFilter = "";
      this.loadPermissions();
    },
    clearSelection() {
      this.selectedMail = "";
      this.permData = null;
      this.message = null;
      this.showUserList = true;
    },
    loadUsers() {
      API.getPermissionUsers((r) => { this.users = r; });
    },
    loadPermissions() {
      if (!this.selectedMail) { this.permData = null; return; }
      API.getPermissions(this.selectedMail, (r) => { this.permData = r; });
    },
    permissionValue(user, key) {
      const permission = (user.permissions || []).find(p => p.key === key);
      return permission ? permission.value : 0;
    },
    permissionLevelLabel(key, value) {
      const level = this.permissionLevels(key).find(option => option.value === Number(value));
      return level ? level.label : value;
    },
    permissionLevels(key) {
      if (key === "Customers") {
        return [
          { value: 0, label: "Kein Zugriff" }, { value: 1, label: "Lesen" },
          { value: 2, label: "Hinzufügen" }, { value: 3, label: "Ändern" },
          { value: 4, label: "Löschen" }, { value: 5, label: "Imitieren" },
        ];
      }
      if (key === "Intern" || key === "Permissions" || key === "Accounts" || key === "Exporte" ||
          key === "Import" || key === "Maintenance" || key === "Announcements" || key === "Newsletter" ||
          key === "Maillist" || key === "Attendance" || key === "Mealplan" || key === "Dashboard" ||
          key === "UploadProjectFiles" || key === "ViewUserFiles") {
        return [{ value: 0, label: "Kein Zugriff" }, { value: 1, label: "Zugriff" }];
      }
      return [
        { value: 0, label: "Kein Zugriff" }, { value: 1, label: "Lesen" },
        { value: 2, label: "Hinzufügen" }, { value: 3, label: "Ändern" },
        { value: 4, label: "Löschen" },
      ];
    },
    savePermissions() {
      this.saving = true;
      this.message = null;
      const data = {};
      for (const field of this.permissionFields) {
        data[field.key] = parseInt(this.permData[field.key]);
      }
      API.updatePermissions(this.selectedMail, data, (r, status) => {
        this.saving = false;
        if (status === 200) {
          this.messageSuccess = true;
          this.message = "Berechtigungen gespeichert.";
          this.loadUsers();
        } else {
          this.messageSuccess = false;
          this.message = "Speichern fehlgeschlagen.";
        }
      });
    },
    resetPermissions() {
      if (!this.selectedMail || this.resetting || !confirm("Alle Berechtigungen dieses Nutzers wirklich löschen?")) {
        return;
      }

      this.resetting = true;
      this.message = null;
      API.deletePermissions(this.selectedMail, (r, status) => {
        this.resetting = false;
        if (status === 200) {
          for (const field of this.permissionFields) {
            this.permData[field.key] = 0;
          }
          this.messageSuccess = true;
          this.message = "Alle Berechtigungen wurden zurückgesetzt.";
          this.loadUsers();
        } else {
          this.messageSuccess = false;
          this.message = "Berechtigungen konnten nicht zurückgesetzt werden.";
        }
      });
    },
  },
  created() {
    this.loadUsers();
  },
};
</script>

<style scoped>
label { display: block; font-weight: bold; margin-bottom: 4px; }
</style>
